'use strict';
//***********************************************************************************
//***********************************************************************************
//******     CN-Map    **************************************************************
//******     Copyright(C) 2019-2020 EnerBIM                        ******************
//***********************************************************************************
//***********************************************************************************

//***********************************************************************************
//***********************************************************************************
//**** Object instance
//***********************************************************************************
//***********************************************************************************

import { fh_matrix, fh_polygon } from '@acenv/fh-3d-viewer';
import { cn_bbp_geometry } from '../utils/cn_bbp_geometry';
import {
    cn_add,
    cn_box,
    cn_clone,
    cn_mul,
    cn_normal,
    cn_normalize,
    cn_polar,
    cn_sub,
    cnx_clone
} from '../utils/cn_utilities';
import { cn_image_dir } from '../utils/image_dir';
import { cn_3d_building } from './cn_3d_building';
import { cn_building } from './cn_building';
import { cn_element } from './cn_element';
import { cn_storey } from './cn_storey';

export class cn_parking_space extends cn_element {
    constructor(scene) {
        super(scene);
        this.class_name = 'cn_parking_space';
        this.scene = scene;

        //*** Model data
        this.vertices = [];

        this.widths = [2.3, 3.2];
        this.length = 5;
        this.angle = 0;

        this.indices = [];
    }

    //***********************************************************************************
    //**** serialize
    //***********************************************************************************
    serialize() {
        var json = this.serialize_element();
        json.vertices = this.vertices.map(v => cn_clone(v));

        json.indices = this.indices;
        json.widths = this.widths;
        json.length = this.length;
        json.angle = this.angle;
        return json;
    }

    static unserialize(json, scene) {
        if (typeof (json.ID) != 'string') return false;

        var parking_space = new cn_parking_space(scene);
        parking_space.unserialize_element(json);
        scene.parking_spaces.push(parking_space);

        parking_space.vertices = json.vertices.map(v => cn_clone(v));

        if (typeof (json.width) == 'object')
            parking_space.widths = json.widths;

        if (typeof (json.length) == 'number')
            parking_space.length = json.length;

        if (typeof (json.angle) == 'number')
            parking_space.angle = json.angle;

        if (typeof (json.indices) == 'object')
            parking_space.indices = json.indices;

        return parking_space;
    }

    //***********************************************************************************
    //**** Draw
    //***********************************************************************************
    draw(camera, add_classes = [], pmr_b64 = {}) {
        let draw_class = "parking_space " + add_classes.join(" ");
        let html = ""; this.get_contours().forEach((ctr, index) => {
            if (index < this.indices.length && this.indices[index]) {
                const dx = cn_sub(ctr[ctr.length - 1], ctr[0]);
                let center = [0, 0];
                ctr.forEach(v => center = cn_add(center, v));
                center = cn_mul(center, 1 / ctr.length);
                const sc = camera.world_to_screen(center);
                const sign_size = camera.world_to_screen_scale;
                html += `<g transform="translate(${sc[0]},${sc[1]}) rotate(${-cn_polar(dx)[1] * 180 / Math.PI}) translate(${-0.5 * sign_size},${-0.5 * sign_size})">`;
                html += `<image xlink:href="${pmr_b64['pmr'] || cn_image_dir()+'pmr.jpg'}" width='${sign_size}' height='${sign_size}' preserveAspectRatio='none' /></g>`;
            }
            html += camera.draw_path(ctr, draw_class, true);
        });
        return html;
    }

    draw_parking_space(camera, index) {
        if (index < 0) return '';
        return camera.draw_path(this.get_contours()[index], "parking_space_mouseover", true);
    }

    //***********************************************************************************
    //**** Draw contour
    //***********************************************************************************
    /**
     * Returns list of points that form the contour
     * @returns {number[][]}
     */
    get_contours() {
        // @ts-ignore
        return this.get_lazy_data(() => {
            let contours = [];
            let line = null;
            let x = 0;
            let dx = [0, 0];
            let max_x = 0;
            let vi = -1;
            let orientation = 0;
            let w = (this.angle == 90) ? this.length : this.widths[0] / Math.cos(this.angle * Math.PI / 180);
            let l = (this.angle == 90) ? this.widths[0] : this.length;

            let w_pmr = (this.angle == 90) ? this.length : this.widths[1] / Math.cos(this.angle * Math.PI / 180);
            let l_pmr = (this.angle == 90) ? this.widths[1] : this.length;

            let deltax = (this.angle < 90) ? this.length * Math.sin(this.angle * Math.PI / 180) : 0;
            let index = 0;
            let is_pmr = (index < this.indices.length && this.indices[index]);
            while (true) {
                if (x >= max_x) {
                    vi++;
                    if (vi >= this.vertices.length - 1) break;
                    dx = cn_sub(this.vertices[vi + 1], this.vertices[vi]);
                    x -= max_x;
                    max_x = cn_normalize(dx);
                    const ori = cn_polar(dx)[1];
                    let delta = ori - orientation;
                    while (delta > Math.PI) delta -= 2 * Math.PI;
                    while (delta < -Math.PI) delta += 2 * Math.PI;
                    if (vi == 0 || Math.abs(delta) > Math.PI / 3) {
                        x = 0;
                        line = null;
                    }
                    orientation = ori;
                }
                const p0 = cn_add(this.vertices[vi], cn_mul(dx, x));
                const p1 = cn_add(p0, cn_add(cn_mul(dx, deltax), cn_mul(cn_normal(dx), -l)));
                const p2 = cn_add(p0, cn_add(cn_mul(dx, deltax), cn_mul(cn_normal(dx), -l_pmr)));
                if (line) {
                    if (is_pmr)
                        contours.push([line[0], line[2], p2, p0]);
                    else
                        contours.push([line[0], line[1], p1, p0]);
                    index++;
                    is_pmr = (index < this.indices.length && this.indices[index]);
                }
                line = [p0, p1, p2];
                x += (is_pmr) ? w_pmr : w;
            }
            return contours;
        }, "contour", "", this);
    }

    /**
     * Returns a polygon for each parking space
     * @returns {fh_polygon[]}
     */
    get_polygons() {
        // @ts-ignore
        return this.get_lazy_data(() => {
            return this.get_contours().map(ctr => {
                const pg = new fh_polygon([0, 0, 0], [0, 0, 1]);
                pg.add_contour(ctr.map(v => cnx_clone(v)));
                return pg;
            });
        }, "polygons", "", this);
    }

    /**
     * Returns the bounding box
     * @returns {cn_box}
     */
    get_bounding_box() {
        // @ts-ignore
        return this.get_lazy_data(() => {
            const box = new cn_box();
            this.get_contours().forEach(ctr => ctr.forEach(p => box.enlarge_point(p)));
            return box;
        }, "bounding_box", "", this);
    }

    /**
     * Returns true if inside the parking space
     * @param {number[]} pt
     * @returns {boolean}
     */
    contains(pt) {
        if (!this.get_bounding_box().contains_point(pt)) return false;

        const pt3d = cnx_clone(pt);
        return !!this.get_polygons().find(pg => pg.contains(pt3d));
    }

    /**
     * Returns the index of the current parking space
     * @param {number[]} pt
     * @returns {number}
     */
    find_space_index(pt) {
        const pt3d = cnx_clone(pt);
        return this.get_polygons().findIndex(pg => pg.contains(pt3d));
    }

    /**
     * Build bbp objects for the space
     * @param {cn_storey} storey
     * @param {boolean} cnmap_pointers
     * @returns {object[]}
     */
    build_bbp_objects(storey, cnmap_pointers = false) {
        const bbp_objects = [];
        const topography = (storey.exterior) ? storey.building.topography : null;
        this.get_contours().forEach((ctr, index) => {
            const bbp_parking_space = {};
            bbp_objects.push(bbp_parking_space);
            bbp_parking_space.ID = cn_building.create_unique_id(storey, this, index);
            const is_pmr = (index < this.indices.length && this.indices[index]);
            bbp_parking_space.Name = "Place de parking";
            if (is_pmr) bbp_parking_space.Name += " handicapé";
            bbp_parking_space.IfcType = 'IfcSite';
            bbp_parking_space.geometries = [];
            bbp_parking_space.geometries.push(this._build_line_geometry(ctr[0], ctr[1], topography));
            bbp_parking_space.geometries.push(this._build_line_geometry(ctr[1], ctr[2], topography));
            bbp_parking_space.geometries.push(this._build_line_geometry(ctr[0], ctr[3], topography));
            bbp_parking_space.geometries.push(this._build_line_geometry(ctr[3], ctr[2], topography));
            if (is_pmr) {
                let dx = cn_sub(ctr[ctr.length - 1], ctr[0]);
                cn_normalize(dx);
                dx = cn_mul(dx, 0.5);
                const dy = cn_normal(dx);
                let center = [0, 0];
                ctr.forEach(v => center = cn_add(center, v));
                center = cn_mul(center, 1 / ctr.length);
                const contour_2d = [];
                contour_2d.push(cn_sub(center, cn_add(dx, dy)));
                contour_2d.push(cn_add(center, cn_sub(dx, dy)));
                contour_2d.push(cn_add(center, cn_add(dx, dy)));
                contour_2d.push(cn_sub(center, cn_sub(dx, dy)));
                const contour = contour_2d.map(v => cnx_clone(v, (topography) ? topography.compute_height(v) + 0.01 : 0));

                const geometry = new cn_bbp_geometry(false);
                bbp_parking_space.geometries.push(geometry);
                geometry.vertices = geometry.vertices.concat(contour.flat());
                geometry.triangles.push(0, 1, 2);
                geometry.triangles.push(0, 2, 3);
                geometry.color = [1, 1, 1, 1];
                geometry.texture = cn_image_dir()+"pmr.jpg";
                const texture_matrix = new fh_matrix();
                texture_matrix.load_inv_axis(contour[0], cnx_clone(cn_mul(dx, 2)), cnx_clone(cn_mul(dy, 2)), [0, 0, 1]);
                geometry.texture_matrix = texture_matrix.values;
            }
            if (cnmap_pointers) {
                bbp_parking_space.cnmap_element = this;
                bbp_parking_space.cnmap_storey = storey;
            }
        });
        return bbp_objects;
    }

    _build_line_geometry(p0, p1, topography) {
        const dy = cn_sub(p1, p0);
        const l = cn_normalize(dy);
        const dx = cn_normal(dy);
        const step = 0.6;
        const width = 0.1;
        const length = 0.3;
        const geometry = new cn_bbp_geometry(false);
        geometry.color = [1, 1, 1, 1];
        geometry.views = ["3d"];
        let nb_vertices = 0;
        for (var y = 0; y < l; y += step) {
            const v0 = cn_add(p0, cn_mul(dy, y));
            const y1 = (y + length < l) ? y + length : l;
            const v1 = cn_add(p0, cn_mul(dy, y1));
            const contour_2d = [];
            contour_2d.push(cn_sub(v0, cn_mul(dx, 0.5 * width)));
            contour_2d.push(cn_sub(v1, cn_mul(dx, 0.5 * width)));
            contour_2d.push(cn_add(v1, cn_mul(dx, 0.5 * width)));
            contour_2d.push(cn_add(v0, cn_mul(dx, 0.5 * width)));
            const contour = contour_2d.map(v => cnx_clone(v, (topography) ? topography.compute_height(v) + 0.01 : 0));
            geometry.vertices = geometry.vertices.concat(contour.flat());
            geometry.triangles.push(nb_vertices, nb_vertices + 1, nb_vertices + 2);
            geometry.triangles.push(nb_vertices, nb_vertices + 2, nb_vertices + 3);
            nb_vertices += 4;
        }
        return geometry;
    }

    /**
     * Update parking spaces
     * @param {cn_3d_building} building_3d
     * @param {cn_storey} storey
     */
    update_3d_geometry(building_3d, storey) {
        building_3d.remove_objects(building_3d.get_3d_objects(this));
        building_3d.add_bbp_objects(this.build_bbp_objects(storey, true));
    }
}

